import styled from '@emotion/styled';
import { Checkbox, message } from 'antd';
import DecorateTitle from 'components/DecorateTitle';
import StatusSwitch from 'components/StatusSwitch';
import { useNouseMessageTypeLabelMap } from 'views/Conversation/Chat/components/MessageType/FoldMsg';
import {
  useReceptionConfig,
  useSetReceptionConfig,
} from '../ConversationSetting/useReceptionConfig';
import { updateUserConfig } from 'services/api/admin';
import { useEffect, useRef } from 'react';
import { useSystemModalActiveFeature } from 'hooks/useSystemModal';
import { queries } from 'services/queries';
import { useQuery } from '@tanstack/react-query';

function SimpleMode() {
  const { t, typeLabelMap } = useNouseMessageTypeLabelMap();

  const activeFeature = useSystemModalActiveFeature();
  const receptionConfig = useReceptionConfig();

  const setReceptionConfig = useSetReceptionConfig();

  const { data: userConfig } = useQuery({
    ...queries.accounts.userConfig,
    enabled: !receptionConfig,
  });

  useEffect(() => {
    if (userConfig?.receptionConfig) {
      setReceptionConfig(userConfig.receptionConfig);
    }
  }, [userConfig?.receptionConfig, setReceptionConfig]);

  const ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (activeFeature === 'simpleMode') {
      setTimeout(() => {
        if (ref.current) {
          ref.current.scrollIntoView();
        }
      }, 1000);
    }
  }, [activeFeature]);

  const configRef = useRef(receptionConfig);
  configRef.current = receptionConfig;

  if (!receptionConfig) {
    return <></>;
  }

  const { simpleModeConfig } = receptionConfig;
  const { simpleMode, simpleModeItems } = simpleModeConfig;

  return (
    <>
      <DecorateTitle ref={ref}>{t('简洁模式')}</DecorateTitle>
      <Content>
        <div>
          <StatusSwitch
            size="small"
            checked={simpleMode}
            onChange={(checked) => {
              return updateUserConfig({
                ...receptionConfig,
                simpleModeConfig: {
                  ...simpleModeConfig,
                  simpleMode: checked,
                },
              })
                .then(() => {
                  if (configRef.current) {
                    setReceptionConfig({
                      ...configRef.current,
                      simpleModeConfig: {
                        ...configRef.current.simpleModeConfig,
                        simpleMode: checked,
                      },
                    });
                  }
                })
                .catch(() => {
                  message.error(t('operation.saveFailed'));
                });
            }}
          />
          {t('简洁模式下折叠以下内容')}
        </div>
        <ul>
          {simpleModeItems.map(({ type, checked }) => {
            if (!typeLabelMap[type]) {
              return null;
            }
            return (
              <li key={type}>
                <Checkbox
                  disabled={!simpleMode}
                  checked={checked}
                  onChange={({ target: { checked } }) => {
                    const next = {
                      ...receptionConfig,
                      simpleModeConfig: {
                        ...simpleModeConfig,
                        simpleModeItems: simpleModeItems.map((item) => {
                          return type === item.type
                            ? {
                                ...item,
                                checked,
                              }
                            : item;
                        }),
                      },
                    };
                    setReceptionConfig(next);
                    updateUserConfig(next).catch(() => {
                      if (configRef.current) {
                        setReceptionConfig({
                          ...configRef.current,
                          simpleModeConfig: {
                            ...configRef.current.simpleModeConfig,
                            simpleModeItems: configRef.current.simpleModeConfig.simpleModeItems.map(
                              (item) => {
                                return type === item.type
                                  ? {
                                      ...item,
                                      checked: !checked,
                                    }
                                  : item;
                              },
                            ),
                          },
                        });
                      }
                      message.error(t('operation.saveFailed'));
                    });
                  }}
                >
                  {typeLabelMap[type]}
                </Checkbox>
              </li>
            );
          })}
        </ul>
      </Content>
    </>
  );
}

const Content = styled.div`
  > div {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    margin-bottom: 14px;
    color: #454d5a;
    background: #f5f8ff;
    > button {
      margin-right: 8px;
    }
  }
  > ul {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    list-style: none;
    padding: 0;
    margin: 0;
    > li {
      flex: 0 0 auto;
      width: 25%;
      margin-bottom: 14px;
    }
  }
`;

export default SimpleMode;
